import GlobalStyles from '@mui/material/GlobalStyles';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import { deDE as deDECore } from '@mui/material/locale';
import { type Palette, type PaletteColor, type Theme, darken, hexToRgb, lighten, rgbToHex } from '@mui/material/styles';
import MuiThemeProvider from '@mui/material/styles/ThemeProvider';
import createTheme from '@mui/material/styles/createTheme';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { deDE } from '@mui/x-data-grid-pro';
import { deDE as deDEPickers } from '@mui/x-date-pickers-pro';
import { merge } from 'es-toolkit/compat';
import React, { type ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { type FluxStore } from '../_flux/Store';
import sFarbschema from '../_json/s_farbschema';
import { getColor } from '../_tools/ColorTools';
import { getFarbeFirma } from '../_tools/Session';
import { checkUndefinedNullOrEmpty } from '../_tools/Tools';

export type CustomPaletteColor = PaletteColor & {
    breeze: string;
    lightest: string;
    lighter: string;
    darker: string;
    darkest: string;
    shadowS: string;
    shadowL: string;
};

export type CustomPalette = Palette & {
    primary: CustomPaletteColor;
    secondary: CustomPaletteColor;
    error: CustomPaletteColor;
    warning: CustomPaletteColor;
    info: CustomPaletteColor;
    success: CustomPaletteColor;
    crisp: CustomPaletteColor;
};

export type CustomTheme = Theme & {
    palette: CustomPalette;
};

type ThemeProviderProps = {
    children: ReactNode;
};

const globalStyle = {
    '@keyframes mui-auto-fill': { from: { display: 'block' } },
    '@keyframes mui-auto-fill-cancel': { from: { display: 'block' } },
    '.hiddenTable': { display: 'none !important' },
    '.materialIcon': { lineHeight: 1 },
};

const getColorScheme = (color: string, theme: Theme): CustomPaletteColor => {
    const colorShadow = hexToRgb(color).replace('rgb(', '').replace(')', '');

    return {
        breeze: rgbToHex(lighten(color, 0.85)),
        lightest: rgbToHex(lighten(color, 0.35)),
        lighter: rgbToHex(lighten(color, 0.2)),
        light: rgbToHex(lighten(color, 0.1)),
        main: color,
        dark: rgbToHex(darken(color, 0.1)),
        darker: rgbToHex(darken(color, 0.2)),
        darkest: rgbToHex(darken(color, 0.35)),
        contrastText: theme.palette.getContrastText(color),
        shadowS: '0px 3px 3px -2px rgba(' + colorShadow + ',0.2),0px 3px 4px 0px rgba(' + colorShadow + ',0.14),0px 1px 8px 0px rgba(' + colorShadow + ',0.12)',
        shadowL: '0px 4px 5px -2px rgba(' + colorShadow + ',0.2),0px 7px 10px 1px rgba(' + colorShadow + ',0.14),0px 2px 16px 1px rgba(' + colorShadow + ',0.12)',
    };
};
export const errorColor = '#f44336';
export const warningColor = '#ff9800';
export const successColor = '#4caf50';
export const infoColor = '#00acc1';
export const crispColor = '#009fde';

function ThemeProvider(props: ThemeProviderProps) {
    const { children } = props;
    const [color, setColor] = useState<string>('#000');
    const [cachedPathname, setCachedPathname] = useState<string>('');
    const loc = useLocation();
    const settings = useSelector((store: FluxStore) => store.userSettings?.main);
    const theme: CustomTheme = useTheme();
    const xs = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
    const sm = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
    const widthSidebar = xs ? 0 : sm ? 48 : 56;

    useEffect(
        function () {
            let col = '#000';
            const colorObj = sFarbschema[getFarbeFirma()];

            if (cachedPathname !== loc?.pathname) {
                if (loc?.pathname.startsWith('/dashboard') || loc?.pathname.startsWith('/login')) col = getColor(colorObj, 'dashboard');
                else if (loc?.pathname.startsWith('/leads')) col = getColor(colorObj, 'leads');
                else if (loc?.pathname.startsWith('/vehicles')) col = getColor(colorObj, 'vehicles');
                else if (loc?.pathname.startsWith('/clients') || loc?.pathname.startsWith('/firmen')) col = getColor(colorObj, 'client');
                else if (loc?.pathname.startsWith('/qr')) col = getColor(colorObj, 'qr');
                else if (loc?.pathname.startsWith('/bdc')) col = getColor(colorObj, 'bdc');
                else col = getColor(colorObj, 'other');
                setCachedPathname(loc?.pathname);
            }

            setColor(col);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [loc?.pathname]
    );

    const defaultTheme = createTheme({ palette: { mode: settings?.darkMode ? 'dark' : 'light' } }, deDE, deDEPickers, deDECore);

    if (!checkUndefinedNullOrEmpty(color)) {
        const newTheme = createTheme(
            merge(defaultTheme, {
                palette: {
                    mode: settings?.darkMode ? 'dark' : 'light',
                    background: {
                        default: settings?.darkMode ? '#303030' : '#eee',
                        paper: settings?.darkMode ? '#101010' : '#fdfdfd',
                    },
                    primary: getColorScheme(color, defaultTheme),
                    error: getColorScheme(errorColor, defaultTheme),
                    warning: getColorScheme(warningColor, defaultTheme),
                    success: getColorScheme(successColor, defaultTheme),
                    info: getColorScheme(infoColor, defaultTheme),
                    crisp: getColorScheme(crispColor, defaultTheme),
                },
                shape: {
                    borderRadius: 4,
                },
            } as CustomTheme)
        );
        newTheme.typography = merge(newTheme.typography, {
            h1: { color: newTheme.palette.text.primary, fontWeight: 400, fontSize: '2.25em', marginTop: 20, marginBottom: 10 },
            h2: { color: newTheme.palette.text.primary, fontWeight: 400, fontSize: '2.05em', marginTop: 20, marginBottom: 10 },
            h3: { color: newTheme.palette.text.primary, fontWeight: 400, fontSize: '1.85em', marginTop: 20, marginBottom: 10 },
            h4: { color: newTheme.palette.text.primary, fontWeight: 400, fontSize: '1.65em', marginTop: 10, marginBottom: 10 },
            h5: { color: newTheme.palette.text.primary, fontWeight: 400, fontSize: '1.45em', marginTop: 10, marginBottom: 10 },
            h6: { color: newTheme.palette.text.primary, fontWeight: 400, fontSize: '1.25em', marginTop: 5, marginBottom: 5 },
            body1: { color: newTheme.palette.text.primary, fontWeight: 300, fontSize: '1em' },
            body2: { color: newTheme.palette.text.secondary, fontWeight: 300, fontSize: '1em' },
        } as CustomTheme['typography']);
        newTheme.components = merge(
            newTheme.components,
            {
                MuiAccordionSummary: {
                    styleOverrides: {
                        content: { alignItems: 'center' },
                    },
                },
                MuiAppBar: {
                    styleOverrides: {
                        root: {
                            backgroundColor: color,
                        },
                    },
                },
                MuiAutocomplete: {
                    styleOverrides: {
                        listbox: { padding: 6 },
                        option: {
                            'lineHeight': 2,
                            'borderRadius': 3,
                            'transition': 'all 200ms linear',
                            '& .MuiSvgIcon-root': {
                                color: defaultTheme.palette.text.primary,
                            },
                            '&:hover': {
                                'backgroundColor': color + '!important',
                                'color': '#fff',
                                'boxShadow':
                                    '0 4px 20px 0 rgba(' +
                                    hexToRgb('#000').replace('rgb(', '').replace(')', '') +
                                    ',.14), 0 7px 10px -5px rgba(' +
                                    hexToRgb(color).replace('rgb(', '').replace(')', '') +
                                    ',.4)',
                                '& .MuiSvgIcon-root': {
                                    color: '#fff',
                                },
                            },
                            '&[aria-selected="true"]': {
                                'backgroundColor': color + '!important',
                                'color': '#fff',
                                'boxShadow':
                                    '0 4px 20px 0 rgba(' +
                                    hexToRgb('#000').replace('rgb(', '').replace(')', '') +
                                    ',.14), 0 7px 10px -5px rgba(' +
                                    hexToRgb(color).replace('rgb(', '').replace(')', '') +
                                    ',.4)',
                                '& .MuiSvgIcon-root': {
                                    color: '#fff',
                                },
                            },
                        },
                        popper: { minWidth: 250 },
                    },
                },
                MuiBadge: {
                    styleOverrides: {
                        badge: {
                            '.iconBadge &': {
                                fontSize: '1rem',
                                backgroundColor: settings?.darkMode ? '#424242' : '#fff',
                                borderRadius: 200,
                                padding: 6,
                                width: 13,
                                height: 13,
                            },
                        },
                    },
                },
                MuiBottomNavigation: {
                    styleOverrides: {
                        root: {
                            width: 'calc(100% - ' + widthSidebar + 'px)',
                            position: 'fixed',
                            bottom: 0,
                            zIndex: 99,
                            right: 0,
                            boxShadow: '0px -2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
                            backgroundColor: color,
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        textPrimary: { color: settings?.darkMode ? rgbToHex(lighten(color, 0.2)) : color },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: { position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'visible' },
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        root: { color: '#fff', backgroundColor: color },
                        deleteIcon: { color: 'rgba(255, 255, 255, 0.6)' },
                    },
                },
                MuiDataGrid: {
                    styleOverrides: {
                        root: {
                            'border': 'none',
                            '&.dataGridClickCell .MuiDataGrid-row': {
                                cursor: 'pointer',
                            },
                        },
                        cell: {
                            '&.MuiDataGrid-cell:focus, &.MuiDataGrid-cell:focus-within': {
                                outline: 'none',
                            },
                        },
                        columnHeaders: {
                            '.disableHeader &': {
                                display: 'none',
                            },
                        },
                        detailPanel: {
                            padding: theme.spacing(2),
                        },
                        virtualScroller: {
                            '.disableHeader &': {
                                marginTop: '0 !important',
                            },
                        },
                    },
                },
                MuiDateRangePickerInput: {
                    styleOverrides: {
                        root: {
                            width: '100%',
                        },
                    },
                },
                MuiDialogContent: {
                    styleOverrides: {
                        root: { whiteSpace: 'pre-wrap' },
                    },
                },
                MuiFab: {
                    styleOverrides: {
                        secondary: { backgroundColor: newTheme?.palette?.background.paper, color: newTheme?.palette?.text?.primary },
                    },
                },
                MuiFormControlLabel: {
                    styleOverrides: {
                        labelPlacementEnd: {
                            '.inputCheck &': {
                                marginRight: 0,
                            },
                        },
                    },
                },
                MuiGrid: {
                    styleOverrides: {
                        container: {
                            '&.MuiGrid-spacing-xs-2.gridTable': {
                                'margin': -8,
                                '& > .MuiGrid-item': {
                                    padding: 8,
                                },
                            },
                        },
                    },
                },
                MuiIconButton: {
                    styleOverrides: {
                        root: {
                            'padding': 8,
                            '.MuiInputAdornment-root &': {
                                padding: 4,
                            },
                            '.MuiDataGrid-root .MuiDataGrid-cell &': {
                                padding: 2,
                            },
                        },
                        colorPrimary: {
                            color: settings?.darkMode ? rgbToHex(lighten(color, 0.2)) : color,
                        },
                    },
                },
                MuiInputBase: {
                    defaultProps: {
                        disableInjectingGlobalStyles: true,
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        outlined: {
                            '&:not(.MuiInputLabel-shrink)': {
                                transform: 'translate(14px, 9px) scale(1)',
                            },
                            '.MuiTextField-root.outlinedStartAdornment&, .MuiFormControl-root.outlinedStartAdornment&': {
                                '&.MuiInputLabel-shrink': {
                                    transform: 'translate(65px, -6px) scale(0.75)',
                                },
                                'transform': 'translate(65px, 16px) scale(1)',
                            },
                        },
                    },
                },
                MuiLink: {
                    defaultProps: {
                        underline: 'hover',
                    },
                    styleOverrides: {
                        root: { color: defaultTheme.palette.text.primary },
                    },
                },
                MuiList: {
                    styleOverrides: {
                        padding: { padding: 6 },
                    },
                },
                MuiListIcon: {
                    styleOverrides: {
                        root: {
                            minWidth: 0,
                        },
                    },
                },
                MuiListItemIcon: {
                    styleOverrides: {
                        root: {
                            ':not(.mainNavButton) &': {
                                '&:hover': {
                                    backgroundColor: color + '!important',
                                    color: '#fff',
                                    boxShadow:
                                        '0 4px 20px 0 rgba(' +
                                        hexToRgb('#000').replace('rgb(', '').replace(')', '') +
                                        ',.14), 0 7px 10px -5px rgba(' +
                                        hexToRgb(color).replace('rgb(', '').replace(')', '') +
                                        ',.4)',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: color + '!important',
                                    color: '#fff',
                                },
                            },
                        },
                    },
                },
                MuiListItemText: {
                    styleOverrides: {
                        root: {
                            ':not(.mainNavButton) &': {
                                '&:hover': {
                                    backgroundColor: color + '!important',
                                    color: '#fff',
                                    boxShadow:
                                        '0 4px 20px 0 rgba(' +
                                        hexToRgb('#000').replace('rgb(', '').replace(')', '') +
                                        ',.14), 0 7px 10px -5px rgba(' +
                                        hexToRgb(color).replace('rgb(', '').replace(')', '') +
                                        ',.4)',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: color + '!important',
                                    color: '#fff',
                                },
                            },
                        },
                    },
                },
                MuiListItem: {
                    styleOverrides: {
                        root: {
                            '&.Mui-selected:not(.mainNavButton)': {
                                backgroundColor: color + '!important',
                                color: '#fff',
                            },
                        },
                    },
                },
                MuiListSubheader: {
                    styleOverrides: {
                        sticky: { top: -12 },
                    },
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: {
                            'lineHeight': 2,
                            'borderRadius': 3,
                            'transition': 'all 200ms linear',
                            '&:hover': {
                                backgroundColor: color + '!important',
                                color: '#fff',
                                boxShadow:
                                    '0 4px 20px 0 rgba(' +
                                    hexToRgb('#000').replace('rgb(', '').replace(')', '') +
                                    ',.14), 0 7px 10px -5px rgba(' +
                                    hexToRgb(color).replace('rgb(', '').replace(')', '') +
                                    ',.4)',
                            },
                            '&.Mui-selected': {
                                backgroundColor: color + '!important',
                                color: '#fff',
                            },
                        },
                    },
                },
                MuiMobileStepper: {
                    styleOverrides: {
                        root: { backgroundColor: 'transparent' },
                        dot: { backgroundColor: newTheme?.palette?.primary?.light },
                        dotActive: { backgroundColor: '#fff' },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            paddingRight: 4,
                        },
                        input: {
                            padding: theme.spacing(1.1) + ' ' + theme.spacing(1.5),
                            paddingRight: 0,
                        },
                    },
                },
                MuiSlider: {
                    styleOverrides: {
                        root: { marginTop: 8 },
                        marked: { marginBottom: 0 },
                        markLabel: {
                            'top': -12,
                            '@media (pointer: coarse)': {
                                top: -6,
                            },
                        },
                        markLabelActive: {
                            '&:after': {
                                border: '1px solid ' + color,
                            },
                        },
                        valueLabel: {
                            'left': 'auto',
                            'top': -11,
                            'background': color,
                            'borderRadius': '0.4em',
                            'whiteSpace': 'nowrap',
                            'fontSize': '0.75rem',
                            'padding': 0,
                            '&>span': {
                                width: 'auto',
                                padding: '2px 4px',
                                backgroundColor: 'transparent',
                            },
                            '&:after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                width: 0,
                                height: 0,
                                border: '7px solid transparent',
                                borderTopColor: color,
                                borderBottom: 0,
                                borderLeftWidth: 4,
                                borderRightWidth: 4,
                                marginLeft: -4,
                                marginBottom: -4,
                            },
                        },
                    },
                },
                MuiSkeleton: {
                    styleOverrides: {
                        root: { backgroundColor: settings?.darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' },
                        text: { '&:empty:before': { content: '""' } },
                        wave: { '&::after': { background: 'linear-gradient(90deg, transparent, ' + (settings?.darkMode ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)') + ', transparent)' } },
                    },
                },
                MuiSnackbarContent: {
                    styleOverrides: {
                        root: { flexWrap: 'nowrap' },
                    },
                },
                MuiSpeedDialAction: {
                    styleOverrides: {
                        fab: {
                            '&.Mui-disabled': {
                                pointerEvents: 'auto !important',
                            },
                        },
                    },
                },
                MuiStepLabel: {
                    styleOverrides: {
                        root: {
                            '&.Mui-active': {
                                textDecoration: 'underline',
                            },
                        },
                        labelContainer: { lineHeight: '1em' },
                        label: { marginTop: '5px !important' },
                    },
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: { 'fontSize': '1em', 'lineHeight': '1.25em', 'padding': 8, '& .MuiIconButton-root:not(.MuiSwitch-switchBase):not(.MuiRadio-root):not(MuiCheckbox-root)': { margin: -8 } },
                        stickyHeader: { backgroundColor: settings?.darkMode ? '#424242' : '#fff' },
                    },
                },
                MuiTablePagination: {
                    styleOverrides: {
                        displayedRows: { margin: 0 },
                        selectLabel: { margin: 0 },
                        spacer: { display: 'none' },
                        toolbar: { justifyContent: 'center' },
                    },
                },
                MuiTabs: {
                    styleOverrides: {
                        root: {
                            '.customTabs&': {
                                minHeight: 48,
                                margin: 0,
                            },
                        },
                        indicator: {
                            '.navPills &, .customTabs &': {
                                display: 'none',
                            },
                            '.navTabs &': {
                                backgroundColor: '#fff',
                            },
                        },
                    },
                },
                MuiTab: {
                    styleOverrides: {
                        root: {
                            '.customTabs &, .navPills &': {
                                'borderRadius': newTheme.shape.borderRadius,
                                'color': (settings?.darkMode ? newTheme?.palette?.common.white : newTheme?.palette?.text?.secondary) + '!important',
                                'padding': '12px 16px',
                                '&.Mui-selected': {
                                    color: '#fff !important',
                                },
                            },
                            '.customTabs &, .navTabs &': {
                                color: '#fff !important',
                            },
                            '.customTabs &': {
                                minHeight: 0,
                            },
                        },
                    },
                },
                MuiTextField: {
                    defaultProps: {
                        variant: 'standard',
                    },
                },
                MuiToolbar: {
                    styleOverrides: {
                        gutters: { paddingLeft: '8px !important', paddingRight: '8px !important' },
                        tooltip: { maxWidth: 400, whiteSpace: 'pre' },
                    },
                },
            } as CustomTheme['components'],
            deDE,
            deDEPickers,
            deDECore
        );

        document.body.style.background = newTheme?.palette?.background.default;

        return (
            <StyledEngineProvider injectFirst>
                <MuiThemeProvider key="themeProvider" theme={newTheme}>
                    <GlobalStyles styles={globalStyle} />
                    {children}
                </MuiThemeProvider>
            </StyledEngineProvider>
        );
    }
}

export default React.memo(ThemeProvider);
